import { SignatureFrame } from "./SignatureFrame";

interface Lane {
  y: number;
  label: string;
  spans: Array<[from: number, to: number]>;
  gold?: boolean;
}

const SEMS = [1, 2, 3, 4];

const LANES: Lane[] = [
  { y: 80, label: "COURSEWORK", spans: [[1, 2]] },
  { y: 130, label: "ELECTIVES", spans: [[2, 3]], gold: true },
  { y: 180, label: "SEMINAR", spans: [[2, 3]] },
  { y: 230, label: "INTERNSHIP", spans: [[3, 3]] },
  { y: 280, label: "DISSERTATION", spans: [[3, 4]] },
];

const MILESTONES = [
  { s: 2, label: "COURSEWORK DONE" },
  { s: 3, label: "PROJECT START" },
  { s: 4, label: "GRADUATION" },
];

const YEAR_MARKERS = [
  { s: 1.5, label: "YEAR 1" },
  { s: 3.5, label: "YEAR 2" },
];

// Four columns spread across the same canvas the eight-semester UG map uses.
const STEP = 200;
const CELL = 130;
const HALF = CELL / 2;
const semX = (s: number) => 110 + (s - 1) * STEP;

export function PostgraduateMap() {
  return (
    <SignatureFrame
      title="Two years, four semesters — how a PG degree unfolds"
      code="MAP-CUR-004"
      meta="Reference flow"
    >
      <svg viewBox="0 0 920 360" preserveAspectRatio="xMidYMid meet">
        {SEMS.map((s) => (
          <g key={s} className="illo-pop">
            <text x={semX(s) + HALF} y={48} textAnchor="middle" className="illo-label-bold">
              SEM {s.toString().padStart(2, "0")}
            </text>
            <line x1={semX(s)} y1={60} x2={semX(s) + CELL} y2={60} className="illo-stroke" />
          </g>
        ))}

        {SEMS.map((s) => (
          <line
            key={s}
            x1={semX(s) + HALF}
            y1={70}
            x2={semX(s) + HALF}
            y2={310}
            className="illo-stroke"
            strokeWidth="0.5"
            opacity="0.3"
            strokeDasharray="2 4"
          />
        ))}

        {LANES.map((lane, idx) => (
          <g key={idx} className={`illo-pop illo-pop-d${Math.min(idx + 1, 5)}`}>
            <text x="14" y={lane.y + 14} className="illo-label-bold" textAnchor="start">
              {lane.label}
            </text>
            {lane.spans.map(([from, to], i) => {
              const x1 = semX(from);
              const x2 = semX(to) + CELL;
              return (
                <g key={i}>
                  <rect
                    x={x1}
                    y={lane.y}
                    width={x2 - x1}
                    height={28}
                    rx="2"
                    fill={lane.gold ? "var(--gold-500)" : "var(--paper)"}
                    stroke={lane.gold ? "var(--gold-700)" : "var(--navy-800)"}
                    strokeWidth={lane.gold ? "1" : "1.5"}
                  />
                  {Array.from({ length: to - from }, (_, k) => (
                    <line
                      key={k}
                      x1={semX(from + k + 1)}
                      y1={lane.y + 4}
                      x2={semX(from + k + 1)}
                      y2={lane.y + 24}
                      stroke={lane.gold ? "var(--navy-900)" : "var(--ink-300)"}
                      strokeWidth="0.7"
                    />
                  ))}
                </g>
              );
            })}
          </g>
        ))}

        <g className="illo-pop illo-pop-d5">
          {MILESTONES.map((m, i) => (
            <g key={i}>
              <circle cx={semX(m.s) + HALF} cy="325" r="5" className="illo-gold-fill" />
              <line
                x1={semX(m.s) + HALF}
                y1="305"
                x2={semX(m.s) + HALF}
                y2="320"
                className="illo-gold-stroke"
              />
              <text
                x={semX(m.s) + HALF}
                y="350"
                textAnchor="middle"
                className="illo-label-bold"
                fill="var(--gold-700)"
              >
                {m.label}
              </text>
            </g>
          ))}
        </g>

        {YEAR_MARKERS.map((y, i) => (
          <text
            key={i}
            x={semX(y.s) + HALF}
            y={28}
            textAnchor="middle"
            className="illo-numeral"
            style={{ fontSize: "14px" }}
          >
            {y.label}
          </text>
        ))}
      </svg>
    </SignatureFrame>
  );
}
